import React, { useState, useEffect } from 'react';
import { X, Code, Zap, Wrench, Rocket, CheckCircle } from 'lucide-react';

const DevelopmentPopup = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [currentLine, setCurrentLine] = useState(0);

  // Code lines for the animation
  const codeLines = [
    "import { Bangladesh } from 'global-marketplace';",
    "const madeinbd = new Platform({",
    "  mission: 'Connect Bangladesh with World',",
    "  features: ['Authentic Products', 'Verified Sellers'],",
    "  status: 'Building Amazing Experience...',",
    "});",
    "",
    "// Implementing secure payment gateway...",
    "await madeinbd.addFeature('EscrowPayments');",
    "",
    "// Adding AI-powered search...",
    "madeinbd.enableAI('SmartProductMatching');",
    "",
    "// Connecting with suppliers...",
    "const suppliers = await madeinbd.verifySuppliers();",
    "",
    "console.log('🚀 Made in BD launching soon!');",
  ];

  // Show popup on first visit
  useEffect(() => {
    const hasSeenPopup = localStorage.getItem('madeinbd-dev-popup-seen');
    if (!hasSeenPopup) {
      setIsVisible(true);
    }
  }, []);

  // Animate code lines
  useEffect(() => {
    if (isVisible) {
      const interval = setInterval(() => {
        setCurrentLine((prev) => (prev + 1) % codeLines.length);
      }, 800);
      return () => clearInterval(interval);
    }
  }, [isVisible, codeLines.length]);

  const handleClose = () => {
    setIsVisible(false);
    localStorage.setItem('madeinbd-dev-popup-seen', 'true');
  };

  const handleContinue = () => {
    setIsVisible(false);
    localStorage.setItem('madeinbd-dev-popup-seen', 'true');
  };

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden animate-fade-in">
        {/* Header */}
        <div className="bg-gradient-to-r from-orange-500 to-red-500 text-white p-6 relative">
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 p-2 hover:bg-white/20 rounded-full transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
          
          <div className="flex items-center space-x-3 mb-4">
            <div className="bg-white/20 p-3 rounded-full">
              <Code className="h-8 w-8" />
            </div>
            <div>
              <h2 className="text-2xl font-bold">We're Building Something Amazing!</h2>
              <p className="text-orange-100">Made in BD is currently under development</p>
            </div>
          </div>
          
          {/* Status Indicators */}
          <div className="flex flex-wrap gap-4 text-sm">
            <div className="flex items-center space-x-2 bg-white/20 px-3 py-1 rounded-full">
              <Zap className="h-4 w-4 text-yellow-300" />
              <span>Core Features</span>
            </div>
            <div className="flex items-center space-x-2 bg-white/20 px-3 py-1 rounded-full">
              <Wrench className="h-4 w-4 text-blue-300" />
              <span>Payment System</span>
            </div>
            <div className="flex items-center space-x-2 bg-white/20 px-3 py-1 rounded-full">
              <Rocket className="h-4 w-4 text-green-300" />
              <span>AI Integration</span>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Left Side - Message */}
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  🇧🇩 Welcome to Made in BD!
                </h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  We're excited to have you here! Our team is working around the clock to create 
                  the ultimate B2B marketplace connecting Bangladesh with the world.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  While we're still in development, you're welcome to explore our website, 
                  browse through our features, and get a preview of what's coming.
                </p>
              </div>

              {/* Features Preview */}
              <div className="space-y-3">
                <h4 className="font-semibold text-gray-900">What's Coming:</h4>
                <div className="space-y-2">
                  <div className="flex items-center space-x-3">
                    <CheckCircle className="h-5 w-5 text-green-500" />
                    <span className="text-gray-700">Verified Bangladeshi Suppliers</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <CheckCircle className="h-5 w-5 text-green-500" />
                    <span className="text-gray-700">Secure Escrow Payment System</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <CheckCircle className="h-5 w-5 text-green-500" />
                    <span className="text-gray-700">AI-Powered Product Matching</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <CheckCircle className="h-5 w-5 text-green-500" />
                    <span className="text-gray-700">Global Shipping Solutions</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <CheckCircle className="h-5 w-5 text-green-500" />
                    <span className="text-gray-700">24/7 Customer Support</span>
                  </div>
                </div>
              </div>

              {/* Progress Bar */}
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="font-medium text-gray-700">Development Progress</span>
                  <span className="text-orange-600 font-bold">75%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div className="bg-gradient-to-r from-orange-500 to-red-500 h-3 rounded-full transition-all duration-1000 ease-out" style={{ width: '75%' }}></div>
                </div>
              </div>
            </div>

            {/* Right Side - Coding Animation */}
            <div className="bg-gray-900 rounded-lg p-4 text-green-400 font-mono text-sm overflow-hidden">
              <div className="flex items-center space-x-2 mb-4 text-gray-400">
                <div className="flex space-x-1">
                  <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                  <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                </div>
                <span>madeinbd-platform.js</span>
              </div>
              
              <div className="space-y-1 h-64 overflow-hidden">
                {codeLines.map((line, index) => (
                  <div
                    key={index}
                    className={`transition-all duration-300 ${
                      index <= currentLine 
                        ? 'opacity-100 transform translate-x-0' 
                        : 'opacity-30 transform translate-x-4'
                    } ${
                      index === currentLine ? 'text-yellow-400' : ''
                    }`}
                  >
                    <span className="text-gray-500 mr-2">{(index + 1).toString().padStart(2, '0')}</span>
                    {line || '\u00A0'}
                    {index === currentLine && (
                      <span className="inline-block w-2 h-4 bg-yellow-400 ml-1 animate-pulse"></span>
                    )}
                  </div>
                ))}
              </div>
              
              {/* Terminal Output */}
              <div className="mt-4 pt-4 border-t border-gray-700">
                <div className="text-blue-400">$ npm run dev</div>
                <div className="text-green-400">✓ Building amazing B2B experience...</div>
                <div className="text-yellow-400">⚡ Connecting Bangladesh with the world</div>
                <div className="text-green-400 animate-pulse">🚀 Made in BD launching soon!</div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 mt-8 pt-6 border-t border-gray-200">
            <button
              onClick={handleContinue}
              className="flex-1 bg-gradient-to-r from-orange-500 to-red-500 text-white py-3 px-6 rounded-lg font-semibold hover:from-orange-600 hover:to-red-600 transition-all duration-200 transform hover:scale-105 flex items-center justify-center space-x-2"
            >
              <Rocket className="h-5 w-5" />
              <span>Explore Preview</span>
            </button>
            <button
              onClick={handleClose}
              className="flex-1 border-2 border-gray-300 text-gray-700 py-3 px-6 rounded-lg font-semibold hover:border-gray-400 hover:bg-gray-50 transition-colors flex items-center justify-center space-x-2"
            >
              <X className="h-5 w-5" />
              <span>Maybe Later</span>
            </button>
          </div>

          {/* Footer Note */}
          <div className="mt-4 text-center">
            <p className="text-sm text-gray-500">
              Have questions? Contact us at{' '}
              <a href="mailto:hello@madeinbd.com" className="text-orange-600 hover:text-orange-700 font-medium">
                hello@madeinbd.com
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DevelopmentPopup;